import { useEffect, useState, type FormEvent } from 'react'
import { Plus, Pencil, Loader2, CircleCheck, CircleSlash, ReceiptText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from '../../lib/dialogs'
import Modal from '../../components/ui/Modal'
import AdvisorDetailModal from '../../components/advisors/AdvisorDetailModal'
import {
  type AdvisorAgreementType,
  type AdvisorDto,
  type CreateAdvisorInput,
  type UpdateAdvisorInput,
} from '@jr2os/shared'
import {
  MAX_ADVISOR_COMMISSION_PCT,
  fractionToPctInput,
  isOverCommissionCap,
  normalizeAdvisorList,
  pctInputToFraction,
} from '../../lib/advisors'
import { useTableSort } from '../../lib/useTableSort'

export default function Advisors() {
  const { t } = useTranslation(['settings', 'common'])
  const [advisors, setAdvisors] = useState<AdvisorDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<AdvisorDto | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [detail, setDetail] = useState<AdvisorDto | null>(null)
  // Crear, editar, activar/desactivar y el detalle con la liquidación de comisiones son solo del ADMIN (el servidor también
  // lo exige: el resto de roles ve la lista, sin IBAN ni notas, y nada más).
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  const columns = isAdmin ? 7 : 6

  // Orden por columna, solo en memoria: esta pantalla es una pestaña de Catálogos y la URL no guarda la pestaña.
  const { rows: sortedAdvisors, header } = useTableSort(
    advisors,
    {
      name: (a) => a.name,
      email: (a) => a.email,
      organization: (a) => a.organization,
      agreement: (a) => t(`advisors.agreementType.${a.agreementType}`),
      defaultCommission: (a) => a.defaultCommissionPct,
      status: (a) => (a.active ? t('advisors.status.active') : t('advisors.status.inactive')),
    },
    { persist: 'state' }
  )

  const reload = async () => {
    setError(null)
    try {
      const resp = await api.get<unknown>('/advisors')
      setAdvisors(normalizeAdvisorList(resp))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('advisors.errors.load'))
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const onToggleActive = async (a: AdvisorDto) => {
    try {
      await api.patch(`/advisors/${a.id}`, { active: !a.active })
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('advisors.errors.generic'))
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">{t('advisors.title')}</h2>
          <p className="t-muted text-sm mt-1">
            {t('advisors.subtitle')}
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => {
              setEditing(null)
              setShowForm(true)
            }}
            className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 transition-all flex items-center gap-1.5"
          >
            <Plus size={16} /> {t('advisors.newButton')}
          </button>
        )}
      </header>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider t-faint border-b border-base">
              {header('name', t('advisors.table.name'), { className: 'px-4 py-3 font-medium' })}
              {header('email', t('advisors.table.email'), { className: 'px-4 py-3 font-medium' })}
              {header('organization', t('advisors.table.organization'), { className: 'px-4 py-3 font-medium' })}
              {header('agreement', t('advisors.table.agreement'), { className: 'px-4 py-3 font-medium' })}
              {header('defaultCommission', t('advisors.table.defaultCommission'), { className: 'px-4 py-3 font-medium' })}
              {header('status', t('advisors.table.status'), { className: 'px-4 py-3 font-medium' })}
              {isAdmin && (
                <th className="px-4 py-3 font-medium text-right">{t('advisors.table.actions')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {advisors === null ? (
              <tr>
                <td colSpan={columns} className="px-4 py-10 text-center t-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> {t('advisors.loading')}
                </td>
              </tr>
            ) : advisors.length === 0 ? (
              <tr>
                <td colSpan={columns} className="px-4 py-10 text-center t-muted">
                  {t('advisors.empty')}
                </td>
              </tr>
            ) : (
              sortedAdvisors.map((a) => (
                <tr
                  key={a.id}
                  className="border-b border-base/50 last:border-b-0 hover:bg-hover/50"
                >
                  <td className="px-4 py-3 t-primary font-medium">{a.name}</td>
                  <td className="px-4 py-3 t-secondary font-mono text-xs">{a.email ?? '—'}</td>
                  <td className="px-4 py-3 t-secondary">{a.organization ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={[
                        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border',
                        a.agreementType === 'EQUITY'
                          ? 'bg-violet-500/10 border-violet-500/30 text-violet-800 dark:text-violet-200'
                          : 'bg-sky-500/10 border-sky-500/30 text-sky-800 dark:text-sky-200',
                      ].join(' ')}
                    >
                      {t(`advisors.agreementType.${a.agreementType}`)}
                    </span>
                  </td>
                  <td className="px-4 py-3 t-secondary font-mono">
                    {a.defaultCommissionPct != null
                      ? `${fractionToPctInput(a.defaultCommissionPct)} %`
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {a.active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
                        <CircleCheck size={14} /> {t('advisors.status.active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs t-muted">
                        <CircleSlash size={14} /> {t('advisors.status.inactive')}
                      </span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setDetail(a)}
                          title={t('advisors.detail.button')}
                          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                        >
                          <ReceiptText size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(a)
                            setShowForm(true)
                          }}
                          title={t('advisors.actions.edit')}
                          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => void onToggleActive(a)}
                          title={a.active ? t('advisors.actions.deactivate') : t('advisors.actions.activate')}
                          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-amber-700 dark:hover:text-amber-300"
                        >
                          {a.active ? <CircleSlash size={14} /> : <CircleCheck size={14} />}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isAdmin && detail && <AdvisorDetailModal advisor={detail} onClose={() => setDetail(null)} />}

      {isAdmin && showForm && (
        <AdvisorFormModal
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={async () => {
            setShowForm(false)
            await reload()
          }}
        />
      )}
    </div>
  )
}

function AdvisorFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: AdvisorDto | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const { t } = useTranslation(['settings', 'common'])
  const [name, setName] = useState(initial?.name ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [commissionPct, setCommissionPct] = useState(
    fractionToPctInput(initial?.defaultCommissionPct)
  )
  const [organization, setOrganization] = useState(initial?.organization ?? '')
  const [payoutIban, setPayoutIban] = useState(initial?.payoutIban ?? '')
  const [agreementType, setAgreementType] = useState<AdvisorAgreementType>(
    initial?.agreementType ?? 'COMISION_PROYECTO'
  )
  const [active, setActive] = useState(initial?.active ?? true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const overCap = isOverCommissionCap(commissionPct)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (overCap) return
    setError(null)
    setSubmitting(true)
    try {
      // % visible → fracción en el payload (5 → 0.05). Tope 30 % (0.30).
      const defaultCommissionPct = pctInputToFraction(commissionPct)
      if (initial) {
        const payload: UpdateAdvisorInput = {
          name: name.trim(),
          email: email.trim() || null,
          defaultCommissionPct,
          organization: organization.trim() || null,
          payoutIban: payoutIban.trim() || null,
          agreementType,
          active,
        }
        await api.patch(`/advisors/${initial.id}`, payload)
      } else {
        const payload: CreateAdvisorInput = {
          name: name.trim(),
          email: email.trim() || null,
          defaultCommissionPct,
          organization: organization.trim() || null,
          payoutIban: payoutIban.trim() || null,
          agreementType,
          active,
        }
        await api.post('/advisors', payload)
      }
      await onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const fieldErr = body?.issues?.fieldErrors
        const firstFieldErr = fieldErr ? Object.values(fieldErr).flat().find(Boolean) : undefined
        setError(firstFieldErr ?? body?.error ?? t('advisors.errors.save'))
      } else {
        setError(t('advisors.errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60'

  return (
    <Modal open onClose={onClose} title={initial ? t('advisors.modal.editTitle') : t('advisors.modal.newTitle')} maxWidth="max-w-xl">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('advisors.form.name')}</label>
            <input
              required
              minLength={1}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('advisors.form.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('advisors.form.emailPlaceholder')}
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">
              {t('advisors.form.organization')}
            </label>
            <input
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder={t('advisors.form.organizationPlaceholder')}
              className={inputCls}
            />
            <p className="text-[11px] t-faint mt-1">{t('advisors.form.organizationHint')}</p>
          </div>
          <div>
            <label className="block text-sm t-secondary mb-1.5">
              {t('advisors.form.payoutIban')}
            </label>
            <input
              value={payoutIban}
              onChange={(e) => setPayoutIban(e.target.value)}
              placeholder="ES91 2100 0418 4502 0005 1332"
              className={`${inputCls} font-mono`}
            />
            <p className="text-[11px] t-faint mt-1">{t('advisors.form.payoutIbanHint')}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm t-secondary mb-1.5">
            {t('advisors.form.agreementType')}
          </label>
          <select
            value={agreementType}
            onChange={(e) => setAgreementType(e.target.value as AdvisorAgreementType)}
            className={inputCls}
          >
            <option value="COMISION_PROYECTO">{t('advisors.agreementType.COMISION_PROYECTO')}</option>
            <option value="EQUITY">{t('advisors.agreementType.EQUITY')}</option>
          </select>
          {agreementType === 'EQUITY' && (
            <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">
              {t('advisors.form.equityHint')}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('advisors.form.commission')}</label>
            <input
              value={commissionPct}
              onChange={(e) => setCommissionPct(e.target.value)}
              placeholder="5"
              inputMode="decimal"
              max={MAX_ADVISOR_COMMISSION_PCT}
              className={`${inputCls} font-mono ${overCap ? '!border-rose-500/60' : ''}`}
            />
            {overCap ? (
              <p className="text-[11px] text-rose-700 dark:text-rose-300 mt-1">{t('advisors.form.overCap')}</p>
            ) : (
              <p className="text-[11px] t-faint mt-1">{t('advisors.form.capHint')}</p>
            )}
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm t-secondary">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="accent-jr2-500"
              />
              {t('advisors.form.active')}
            </label>
          </div>
        </div>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !name.trim() || overCap}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {initial ? t('common:actions.save') : t('advisors.modal.createButton')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
